import { ConfirmationDialog } from "@admin-ui/components/primitives/application/confirmation-dialog";
import { Badge } from "@admin-ui/components/primitives/base/badges/badges";
import { Button } from "@admin-ui/components/primitives/base/buttons/button";
import { Input } from "@admin-ui/components/primitives/base/input/input";
import { formatDate } from "@admin-ui/utils/format-date";
import { router } from "@inertiajs/react";
import { adminAccountInvitationPath, adminAccountInvitationRegenerationPath, adminUserPath } from "@travily/routes";
import type { AdminAccountsAccountInvitation } from "@travily/types/models";
import { useClipboard } from "@ui/hooks/use-clipboard";
import { Check, Copy01 } from "@untitledui/icons";
import { useState } from "react";

interface InvitationPanelProps {
  accountId: number;
  invitation: AdminAccountsAccountInvitation | null;
}

export function InvitationPanel({ accountId, invitation }: InvitationPanelProps) {
  const { copy, copied } = useClipboard();
  const [pendingAction, setPendingAction] = useState<"revoke" | "regenerate" | null>(null);

  const handleCopy = () => {
    if (invitation?.claim_url) copy(invitation.claim_url);
  };

  const handleRegenerate = () => {
    router.post(adminAccountInvitationRegenerationPath(accountId), {}, { preserveScroll: true });
  };

  const handleRevoke = () => {
    router.delete(adminAccountInvitationPath(accountId), { preserveScroll: true });
  };

  if (!invitation) {
    return <FirstInvitationForm accountId={accountId} />;
  }

  return (
    <>
      <div className="flex flex-col gap-6 rounded-xl border border-secondary bg-primary p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex flex-col gap-1">
            <h3 className="text-md font-semibold text-primary">Invitation</h3>
            <InvitationMeta invitation={invitation} />
          </div>
          <InvitationStatusBadge invitation={invitation} />
        </div>

        {invitation.claimable && (
          <>
            <div className="flex flex-col gap-1.5">
              <p className="text-sm text-secondary">
                Send this link to the invitee via email, WhatsApp, or any other channel.
              </p>
              <div className="flex flex-row items-end gap-2">
                <Input
                  label="Invite link"
                  size="md"
                  value={invitation.claim_url}
                  onChange={() => {}}
                  isReadOnly
                  wrapperClassName="flex-1"
                />
                <Button
                  size="md"
                  color="tertiary"
                  iconLeading={copied ? Check : Copy01}
                  onClick={handleCopy}
                  aria-label="Copy invite link"
                >
                  {copied ? "Copied" : "Copy"}
                </Button>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 border-t border-secondary pt-4">
              <Button size="sm" color="secondary-destructive" onClick={() => setPendingAction("revoke")}>
                Revoke
              </Button>
              <Button size="sm" color="secondary" onClick={() => setPendingAction("regenerate")}>
                Regenerate
              </Button>
            </div>
          </>
        )}

        {invitation.status === "claimed" && <ClaimedDetails invitation={invitation} />}

        {(invitation.status === "revoked" || (invitation.status === "pending" && invitation.expired)) && (
          <div className="border-t border-secondary pt-4">
            <Button size="sm" color="primary" onClick={() => setPendingAction("regenerate")}>
              Issue new invitation
            </Button>
          </div>
        )}
      </div>

      <ConfirmationDialog
        title="Revoke invitation"
        description="The current invite link will stop working immediately. You can generate a new one later."
        confirmLabel="Revoke"
        color="error"
        isOpen={pendingAction === "revoke"}
        onOpenChange={(open) => {
          if (!open) setPendingAction(null);
        }}
        onConfirm={() => {
          handleRevoke();
          setPendingAction(null);
        }}
      />

      <ConfirmationDialog
        title="Regenerate invitation"
        description="The current invite link will stop working and a new one will be issued with the same contact details."
        confirmLabel="Regenerate"
        color="warning"
        isOpen={pendingAction === "regenerate"}
        onOpenChange={(open) => {
          if (!open) setPendingAction(null);
        }}
        onConfirm={() => {
          handleRegenerate();
          setPendingAction(null);
        }}
      />
    </>
  );
}

function FirstInvitationForm({ accountId }: { accountId: number }) {
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const canSubmit = contactName.trim().length > 0 && contactEmail.trim().length > 0;

  const handleCreate = () => {
    if (!canSubmit) return;
    router.post(
      adminAccountInvitationPath(accountId),
      { invitation: { contact_name: contactName, contact_email: contactEmail } },
      { preserveScroll: true },
    );
  };

  return (
    <div className="flex flex-col items-start gap-4 rounded-xl border border-secondary bg-primary p-6">
      <p className="text-sm text-tertiary">
        This account doesn't have an invitation. Name the person who will claim it.
      </p>
      <div className="flex w-full flex-col gap-4">
        <Input
          label="Contact name"
          placeholder="Jane Doe"
          size="md"
          value={contactName}
          onChange={setContactName}
          isRequired
        />
        <Input
          label="Contact email"
          placeholder="jane@example.com"
          type="email"
          size="md"
          value={contactEmail}
          onChange={setContactEmail}
          isRequired
          hint="The invitation can only be claimed from this address."
        />
      </div>
      <Button size="sm" color="primary" onClick={handleCreate} isDisabled={!canSubmit}>
        Generate invitation
      </Button>
    </div>
  );
}

function InvitationStatusBadge({ invitation }: { invitation: AdminAccountsAccountInvitation }) {
  if (invitation.status === "claimed") {
    return (
      <Badge color="success" size="sm">
        Claimed
      </Badge>
    );
  }
  if (invitation.status === "revoked") {
    return (
      <Badge color="gray" size="sm">
        Revoked
      </Badge>
    );
  }
  if (invitation.expired) {
    return (
      <Badge color="warning" size="sm">
        Expired
      </Badge>
    );
  }
  return (
    <Badge color="blue" size="sm">
      Pending
    </Badge>
  );
}

function InvitationMeta({ invitation }: { invitation: AdminAccountsAccountInvitation }) {
  if (invitation.status === "claimed" && invitation.claimed_at) {
    return <p className="text-sm text-tertiary">Claimed {formatDate(invitation.claimed_at)}</p>;
  }
  if (invitation.status === "revoked") {
    return <p className="text-sm text-tertiary">No active invitation.</p>;
  }
  if (invitation.expired) {
    return <p className="text-sm text-tertiary">Expired on {formatDate(invitation.expires_at)}.</p>;
  }
  return (
    <p className="text-sm text-tertiary">
      For {invitation.contact_name} — expires {formatDate(invitation.expires_at)}
    </p>
  );
}

function ClaimedDetails({ invitation }: { invitation: AdminAccountsAccountInvitation }) {
  return (
    <div className="flex flex-col gap-3 border-t border-secondary pt-4">
      <p className="text-sm text-secondary">This invitation was claimed {formatDate(invitation.claimed_at ?? "")}.</p>
      {invitation.claimed_by_user_id && (
        <Button size="sm" color="secondary" href={adminUserPath(invitation.claimed_by_user_id)}>
          View claimer
        </Button>
      )}
    </div>
  );
}
